import { useState } from 'react';
import axios from "axios";
import moment from 'moment';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import InputField from "components/fields/InputField";
import "react-datepicker/dist/react-datepicker.css";
import { FiDownload } from "react-icons/fi";

interface PayloadType {
    [key: string]: any;
}

const CommissionExportModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    userPermissions: any;
    filters: any;
    isReadyToExport: boolean;
    dateFiltersForExport: any;
}> = ({
    setSwalProps,
    isOpen,
    onClose,
    filters,
    isReadyToExport,
    dateFiltersForExport
}) => {
        const [isProcessing, setIsProcessing] = useState<boolean>(false);
        const [receiverEmail, setReceiverEmail] = useState<string>("");
        const agent_id = localStorage.getItem("agentId");

        const isFormValid = receiverEmail !== "";

        const closeModal = async () => {
            setReceiverEmail("");
            onClose();
        };

        const handleSubmit = async () => {
            try {
                setIsProcessing(true);
                const formattedFromDate = moment(filters.from, 'DD/MM/YYYY').format('YYYY-MM-DD 00:00');
                const formattedToDate = moment(filters.to, 'DD/MM/YYYY').format('YYYY-MM-DD 23:59');

                const queryObject: PayloadType = {
                    transaction_type: "reseller_fee_received",
                    is_export: true,
                    from: formattedFromDate,
                    to: formattedToDate,
                    email: receiverEmail,
                    userRole: "agent",
                    username: localStorage.getItem("username"),
                    reseller_id: agent_id,
                    display_mode: "agent"
                };

                if (filters?.status !== "" && filters?.status !== undefined) {
                    queryObject.status = filters?.status;
                }

                if (filters?.asset !== "" && filters?.asset !== undefined) {
                    queryObject.asset = filters?.asset;
                }

                const queryString = Object.keys(queryObject)
                    .map(
                        (key) =>
                            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
                    )
                    .join("&");

                await axios.get(process.env.REACT_APP_API_URL + `/api/transaction/get-all-transactions?${queryString}`)
                    .then(function (response) {
                        if (response && response.data?.status === 'ok') {
                            closeModal();
                            setSwalProps({
                                show: true,
                                icon: "success",
                                title: "Success",
                                html: "Your request has been successfully submitted",
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                    setIsProcessing(false);
                                },
                            });
                        } else {
                            setIsProcessing(false);
                            setSwalProps({
                                show: true,
                                icon: "error",
                                title: "Oops!",
                                html: "Your request could not be submitted successfully, please try again",
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                },
                            });
                        }
                    })
                    .catch(function (error) {
                        console.log('error.response: ', error.response);
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: error.response?.data?.message || "Something went wrong",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    });
            } catch (error: any) {
                setIsProcessing(false);
                setSwalProps({
                    show: true,
                    icon: "error",
                    title: "Oops!",
                    html: "Something went wrong, please try again later.",
                    showConfirmButton: true,
                    didClose: () => {
                        setSwalProps({});
                        setIsProcessing(false);
                    },
                });
            }
        };

        return (
            <>
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                    <FiDownload size={20} />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Export Commission
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        {isReadyToExport
                                            ? "Review your filters and provide an email to receive the export"
                                            : "Submit your filters before proceeding with export"}
                                    </p>
                                </div>
                            </div>

                            {!isReadyToExport && (
                                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                                    To ensure the exported file reflects your latest criteria, make sure the updated filters are submitted before proceeding with export.
                                </div>
                            )}

                            {isReadyToExport && (
                                <>
                                    <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                        <dl className="flex flex-col gap-3 text-sm">
                                            <div className="flex">
                                                <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                    Date
                                                </dt>
                                                <dd className="font-medium text-gray-900 dark:text-white">
                                                    {dateFiltersForExport.from + " - " + dateFiltersForExport.to}
                                                </dd>
                                            </div>

                                            {filters.asset !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Asset
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {filters.asset}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.status !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Status
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {filters.status === "complete" ? "Completed" : filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
                                                    </dd>
                                                </div>
                                            )}
                                        </dl>
                                    </div>

                                    <div className="mb-6">
                                        <InputField
                                            disabled={isProcessing}
                                            variant="auth"
                                            extra="shadow-sm"
                                            label="Please fill in email to receive report"
                                            required={true}
                                            placeholder=""
                                            id="receiver_email"
                                            type="text"
                                            autoComplete="off"
                                            value={receiverEmail}
                                            onChange={(e) => setReceiverEmail(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="btn btn-default"
                                >
                                    Cancel
                                </Button>
                                {isReadyToExport &&
                                    <Button
                                        onClick={() => handleSubmit()}
                                        isDisabled={!isFormValid || isProcessing}
                                        isLoading={isProcessing}
                                        loadingText='Processing...'
                                        className="btn btn-primary"
                                    >
                                        Export
                                    </Button>
                                }
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </>
        );
    };

export default CommissionExportModal;
